/*
 * Sonivra product reviews, driven by ONE Google Sheets URL (theme setting "Reviews sheet URL").
 *
 * Flow: the sheet is fetched as CSV straight from Google (no middleman) -> only rows matching THIS product (by Shopify SKU,
 * any variant) are kept -> only rows with status "approved" and valid fields are published -> the rating summary near the
 * title and the reviews section are both drawn from that one list. Reviews typed into the theme editor (Review blocks) are
 * merged in. Nothing is ever invented: with no sheet and no manual reviews the section stays hidden for shoppers.
 *
 * The ingest core is pure (no DOM) so it can be unit-tested in Node.
 * Ported from the TATAMORI "peak-reviews" engine; namespaced for Sonivra and with all shopper-facing text supplied
 * from locale strings through the config script (cfg.i18n).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && root === window) {
    if (window.SonivraReviews && window.SonivraReviews.__booted) return;
    window.SonivraReviews = api;
    api.boot();
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  /* =====================================================================
   * PURE CORE
   * =================================================================== */

  var ALIASES = {
    status: ['status', 'moderation', 'approval'],
    id: ['review_id', 'id'],
    sku: ['sku', 'skus', 'variant_sku'],
    handle: ['product_handle', 'handle'],
    rating: ['rating', 'stars'],
    title: ['title', 'review_title', 'headline'],
    body: ['body', 'content', 'review', 'review_body', 'text'],
    author: ['author_display', 'author', 'reviewer_name', 'display_name', 'name'],
    date: ['review_date', 'date', 'created_at'],
    verified: ['verified_by_order'],
    images: ['image_urls', 'images', 'image_url', 'reviewer_image_url'],
    rights: ['image_rights_confirmed', 'image_rights']
  };

  var DEFAULT_HOSTS = ['cdn.shopify.com', 'drive.google.com', 'lh3.googleusercontent.com'];
  var MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

  function normHeader(h) {
    return String(h == null ? '' : h).replace(/^﻿/, '').trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  function yes(v) {
    return /^(yes|true|y|1)$/i.test(String(v == null ? '' : v).trim());
  }

  function cleanText(s, keepNewlines) {
    var t = String(s == null ? '' : s).replace(/<[^>]*>/g, ' ').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '').replace(/\r\n?/g, '\n');
    t = keepNewlines ? t.replace(/[ \t]+/g, ' ').replace(/ *\n */g, '\n').replace(/\n{3,}/g, '\n\n') : t.replace(/\s+/g, ' ');
    return t.trim();
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function realDate(y, m, d) {
    var dt = new Date(Date.UTC(y, m - 1, d));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return '';
    return y + '-' + pad(m) + '-' + pad(d);
  }

  /** ISO (2026-10-01), US sheet display (10/1/2026), or "Oct 1, 2026". Relative text ("14 days ago") is never accepted. */
  function parseDate(v) {
    var s = String(v == null ? '' : v).trim();
    var m;
    if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/.exec(s))) return realDate(+m[1], +m[2], +m[3]);
    if ((m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(s))) return realDate(+m[1], +m[2], +m[3]);
    if ((m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s))) return realDate(+m[3], +m[1], +m[2]);
    if ((m = /^([A-Za-z]{3,9})\.? (\d{1,2}),? (\d{4})$/.exec(s)) && MONTHS[m[1].slice(0, 3).toLowerCase()]) return realDate(+m[3], MONTHS[m[1].slice(0, 3).toLowerCase()], +m[2]);
    return '';
  }

  function hostAllowed(url, ctx) {
    var m = /^https:\/\/([^\/?#:]+)(?::\d+)?(?:[\/?#]|$)/i.exec(url);
    if (!m) return !!(ctx && ctx.allowLocal && /^http:\/\/(127\.0\.0\.1|localhost)[:\/]/i.test(url));
    var host = m[1].toLowerCase();
    var hosts = DEFAULT_HOSTS.concat((ctx && ctx.extraHosts) || []);
    return hosts.some(function (h) { return host === h || host.endsWith('.' + h); }) || host.endsWith('.myshopify.com');
  }

  /** Accepts the URL as pasted in the theme editor and returns the CSV URL to fetch. */
  function normalizeUrl(input, opts) {
    var raw = String(input == null ? '' : input).trim();
    if (!raw) return { url: '', error: 'empty' };
    var u;
    try { u = new URL(raw); } catch (e) { return { url: '', error: 'invalid_url' }; }
    var local = opts && opts.allowLocal && /^(127\.0\.0\.1|localhost)$/.test(u.hostname);
    if (u.protocol !== 'https:' && !(local && u.protocol === 'http:')) return { url: '', error: 'https_required' };
    if (u.hostname === 'docs.google.com') {
      var gid = u.searchParams.get('gid') || (/[#&]gid=(\d+)/.exec(u.hash) || [])[1] || '';
      var pub = /^\/spreadsheets\/d\/e\/([^\/]+)/.exec(u.pathname);
      if (pub) {
        var out = new URL('https://docs.google.com/spreadsheets/d/e/' + pub[1] + '/pub');
        out.searchParams.set('output', 'csv');
        if (gid) out.searchParams.set('gid', gid);
        if (u.searchParams.get('single')) out.searchParams.set('single', u.searchParams.get('single'));
        return { url: out.toString(), kind: 'published' };
      }
      var id = /^\/spreadsheets\/d\/([^\/]+)/.exec(u.pathname);
      if (id) {
        var g = 'https://docs.google.com/spreadsheets/d/' + id[1] + '/gviz/tq?tqx=out:csv' + (gid ? '&gid=' + gid : '');
        if (/\/gviz\/tq/.test(u.pathname) && u.searchParams.get('sheet')) g += '&sheet=' + encodeURIComponent(u.searchParams.get('sheet'));
        return { url: g, kind: 'shared' };
      }
      return { url: '', error: 'unrecognized_google_url' };
    }
    return { url: raw, kind: 'other' };
  }

  function parseCsv(text) {
    var s = String(text == null ? '' : text).replace(/^﻿/, '');
    var rows = [], row = [], f = '', q = false, i, c;
    for (i = 0; i < s.length; i++) {
      c = s[i];
      if (q) {
        if (c === '"') { if (s[i + 1] === '"') { f += '"'; i++; } else q = false; }
        else f += c;
      } else if (c === '"') q = true;
      else if (c === ',') { row.push(f); f = ''; }
      else if (c === '\n') { row.push(f); rows.push(row); row = []; f = ''; }
      else if (c !== '\r') f += c;
    }
    if (f.length || row.length) { row.push(f); rows.push(row); }
    return rows.filter(function (r) { return r.some(function (x) { return String(x).trim() !== ''; }); });
  }

  function splitSkus(v) {
    return String(v == null ? '' : v).split(/[,;|\n]+/).map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
  }

  /**
   * table: parseCsv() output. ctx: { skus:[...], handle, today:'YYYY-MM-DD', allowLocal, extraHosts, preview }
   * Returns { reviews, stats }. Nothing is published unless status is explicitly "approved".
   * ctx.preview is for the theme editor and localhost ONLY (the browser layer refuses it for shoppers): it lists this product's
   * pending/undated records so a merchant can see the design, every record carries preview.flags saying why it is not publishable.
   */
  function ingest(table, ctx) {
    var stats = { rows: 0, forProduct: 0, matchedBy: { sku: 0, handle: 0 }, otherProducts: 0, missingSku: 0, rejects: {}, bySku: {}, imagesDropped: 0, fatal: '' };
    var reviews = [];
    if (!table || !table.length) { stats.fatal = 'empty_sheet'; return { reviews: reviews, stats: stats }; }
    var head = table[0].map(normHeader);
    var ix = {};
    Object.keys(ALIASES).forEach(function (k) {
      ix[k] = -1;
      for (var a = 0; a < ALIASES[k].length && ix[k] < 0; a++) ix[k] = head.indexOf(ALIASES[k][a]);
    });
    if (ix.sku < 0 && ix.handle < 0) { stats.fatal = 'no_sku_or_handle_column'; stats.rows = table.length - 1; return { reviews: reviews, stats: stats }; }
    // Without a status column nothing can be approved. We still count matching rows so the diagnostics can say what would match.
    if (ix.status < 0) stats.fatal = 'no_status_column';
    var mySkus = (ctx.skus || []).map(function (s) { return String(s).trim().toLowerCase(); }).filter(Boolean);
    var handle = String(ctx.handle || '').trim().toLowerCase();
    var seenId = {}, seenBody = {};
    function rej(why) { stats.rejects[why] = (stats.rejects[why] || 0) + 1; }

    table.slice(1).forEach(function (r, n) {
      var get = function (k) { return ix[k] >= 0 ? String(r[ix[k]] == null ? '' : r[ix[k]]).trim() : ''; };
      stats.rows++;
      var rowSkus = splitSkus(get('sku'));
      var rowHandle = get('handle').toLowerCase();
      rowSkus.forEach(function (s) { stats.bySku[s] = (stats.bySku[s] || 0) + 1; });

      var how = '';
      if (rowSkus.length) {
        if (rowSkus.some(function (s) { return mySkus.indexOf(s) >= 0; })) how = 'sku';
        else if (rowHandle && rowHandle === handle) { stats.forProduct++; rej('sku_mismatch'); return; }
        else { stats.otherProducts++; return; }
      } else if (rowHandle) {
        if (rowHandle === handle) how = 'handle';
        else { stats.otherProducts++; return; }
      } else { stats.missingSku++; return; }

      stats.forProduct++;
      stats.matchedBy[how]++;
      var preview = !!ctx.preview, flags = [];
      if (ix.status < 0 && !preview) return;

      if (get('status').toLowerCase() !== 'approved') { if (!preview) return rej('status_not_approved'); flags.push('status: ' + (get('status') || 'blank')); }
      var rating = Number(get('rating'));
      if (!(get('rating') !== '' && Number.isInteger(rating) && rating >= 1 && rating <= 5)) return rej('invalid_rating');
      var body = cleanText(get('body'), true);
      if (body.length < 20 || body.length > 4000) return rej('invalid_body_length');
      var author = cleanText(get('author'));
      if (!author || author.length > 40) return rej('invalid_author');
      var dateRaw = get('date');
      var date = parseDate(dateRaw);
      // "unknown" is the owner's explicit declaration that the real date was never recorded (migrated originals). It is shown as "Date not recorded", never guessed.
      if (!date && dateRaw.toLowerCase() === 'unknown') { if (preview) flags.push('date not recorded'); }
      else if (!date && !preview) return rej(dateRaw ? 'unrecognized_date' : 'missing_date');
      if (!date) { date = ''; if (dateRaw.toLowerCase() !== 'unknown') flags.push(dateRaw ? 'date not recognized' : 'date not recorded'); }
      if (date && ctx.today && date > ctx.today) { if (!preview) return rej('future_date'); date = ''; flags.push('date in the future'); }
      var id = get('id') || 'row-' + (n + 2);
      if (seenId[id]) return rej('duplicate_id');
      var key = (author + '|' + body).toLowerCase().replace(/[^a-z0-9]+/g, '');
      if (seenBody[key]) return rej('duplicate_content');
      seenId[id] = true;
      seenBody[key] = true;

      var images = [], held = 0;
      var imgCell = get('images');
      if (imgCell) {
        var rights = yes(get('rights')) || !!ctx.photoConfirm; // photoConfirm = the owner's explicit setting in the theme editor
        if (!rights && !preview) held = imgCell.split(/[|\s,]+/).filter(Boolean).length;
        if (rights || preview) {
          imgCell.split(/[|\s,]+/).filter(Boolean).forEach(function (u) {
            if (images.length < 4 && hostAllowed(u, ctx)) images.push({ url: u }); else stats.imagesDropped++;
          });
          if (!rights && images.length) flags.push('photo permission not confirmed');
        } else stats.imagesDropped++;
      }
      var rv = { id: id, rating: rating, title: cleanText(get('title')).slice(0, 120), body: body, author: author, date: date, verified: yes(get('verified')), images: images };
      if (held) rv.heldPhotos = held;
      if (preview) rv.preview = { flags: flags };
      reviews.push(rv);
    });
    return { reviews: reviews, stats: stats };
  }

  /**
   * Reviews typed into the theme editor (section blocks). items: [{id, author, rating, title, body, date, verified, images:[url], permission, product}]
   * Same safety rules as the sheet, adapted to an owner who is typing: a blank date is allowed ("Date not recorded"), a malformed or future date is not,
   * a photo needs the permission box ticked and an allowed https host, and the Verified badge is exactly the owner's toggle.
   * Returns { reviews, rejects:[{id, why}] } so the theme editor can say what is wrong with a block.
   */
  function ingestManual(items, ctx) {
    var out = [], rejects = [];
    (items || []).forEach(function (it, n) {
      var id = 'manual-' + (it.id || n);
      var why = '';
      var rating = Number(it.rating);
      var body = cleanText(it.body, true);
      var author = cleanText(it.author);
      var date = '';
      var raw = String(it.date == null ? '' : it.date).trim();
      if (raw) { date = parseDate(raw); if (!date) why = 'date must look like 2026-10-24'; else if (ctx && ctx.today && date > ctx.today) why = 'date is in the future'; }
      if (!why && !(Number.isInteger(rating) && rating >= 1 && rating <= 5)) why = 'rating must be 1 to 5';
      if (!why && (body.length < 20 || body.length > 4000)) why = 'review text must be 20 to 4,000 characters';
      if (!why && (!author || author.length > 40)) why = 'reviewer name is required (40 characters max)';
      if (why) { rejects.push({ id: id, why: why }); return; }
      var images = [];
      if (it.permission) (it.images || []).forEach(function (u) { u = String(u || '').replace(/^\/\//, 'https://'); if (u && images.length < 4 && hostAllowed(u, ctx || {})) images.push({ url: u }); });
      out.push({ id: id, rating: rating, title: cleanText(it.title).slice(0, 120), body: body, author: author, date: date, verified: it.verified === true, images: images, manual: true });
    });
    return { reviews: out, rejects: rejects };
  }

  /** One list from several sources. The first copy of a review (same author and text) wins, so an imported review is never doubled by a manual one. */
  function mergeReviews() {
    var seen = {}, out = [];
    Array.prototype.slice.call(arguments).forEach(function (list) {
      (list || []).forEach(function (r) {
        var key = (r.author + '|' + r.body).toLowerCase().replace(/[^a-z0-9]+/g, '');
        if (seen[key]) return;
        seen[key] = true; out.push(r);
      });
    });
    return out;
  }

  function summarize(reviews) {
    var dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, sum = 0;
    reviews.forEach(function (r) { dist[r.rating]++; sum += r.rating; });
    var n = reviews.length;
    var verified = reviews.filter(function (r) { return r.verified; }).length;
    return { count: n, average: n ? sum / n : 0, dist: dist, verified: verified };
  }

  var core = { ALIASES: ALIASES, normalizeUrl: normalizeUrl, parseCsv: parseCsv, parseDate: parseDate, ingest: ingest, ingestManual: ingestManual, mergeReviews: mergeReviews, summarize: summarize, cleanText: cleanText, hostAllowed: hostAllowed };

  /* =====================================================================
   * BROWSER
   * =================================================================== */

  if (typeof document === 'undefined') return core;

  var TTL = 5 * 60 * 1000;
  var STALE_MAX = 24 * 60 * 60 * 1000;
  var CACHE_PREFIX = 'sv_reviews_v1:';
  var shared = {};
  var state = { cfg: null, all: [], sheet: [], manual: [], manualRejects: [], stats: null, source: '', fetchMs: 0, error: '', rating: 0, photos: false, verified: false, sort: 'newest', q: '', shown: 6, loaded: false, preview: false, broken: {} };

  /** Shopper-facing text comes from locale strings passed through the config script (cfg.i18n). {name} placeholders are filled from vars. */
  function T(key, vars) {
    var dict = (state.cfg && state.cfg.i18n) || {};
    var out = dict[key] != null ? String(dict[key]) : key;
    for (var k in (vars || {})) out = out.split('{' + k + '}').join(vars[k]);
    return out;
  }

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var isLocal = /^(127\.0\.0\.1|localhost)$/.test(location.hostname);
  var qs = new URLSearchParams(location.search);

  function track(name, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      var e = { event: name };
      for (var k in (detail || {})) e[k] = detail[k];
      window.dataLayer.push(e);
      if (typeof window.gtag === 'function') window.gtag('event', name, detail || {});
    } catch (e) { /* analytics must never break the page */ }
  }

  function h(tag, attrs) {
    var el = document.createElement(tag);
    attrs = attrs || {};
    for (var k in attrs) {
      var v = attrs[k];
      if (v == null || v === false) continue;
      if (k === 'text') el.textContent = v;
      else if (k === 'class') el.className = v;
      else if (k.slice(0, 2) === 'on') el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (var i = 2; i < arguments.length; i++) {
      var kids = [].concat(arguments[i]);
      for (var j = 0; j < kids.length; j++) if (kids[j] != null) el.append(kids[j].nodeType ? kids[j] : document.createTextNode(kids[j]));
    }
    return el;
  }

  /** Shopify CDN links carry a width parameter: cards and the strip load a small version, the lightbox loads the full one. */
  var thumb = function (u, w) { return /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=' + w) : u; };
  var fmtAvg = function (n) { return (Math.round(n * 10) / 10).toFixed(1); };
  var fmtDate = function (iso) { return !iso ? T('date_unknown') : new Date(iso + 'T00:00:00Z').toLocaleDateString(/^en$/i.test(document.documentElement.lang) || !document.documentElement.lang ? 'en-GB' : document.documentElement.lang, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); };
  var initials = function (name) {
    return name.replace(/[^A-Za-zÀ-ɏ\s.]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map(function (s) { return s[0].toUpperCase(); }).join('') || '?';
  };
  var todayIso = function () { return new Date().toISOString().slice(0, 10); };

  function readConfig() {
    var el = $('script[data-sv-reviews-config]');
    if (!el) return null;
    var c;
    try { c = JSON.parse(el.textContent); } catch (e) { return null; }
    if (!c) return null;
    c.designMode = !!c.designMode;
    var override = qs.get('sv_reviews_url');
    if (override && (isLocal || c.designMode)) c.url = override;
    var ttl = qs.get('sv_reviews_ttl');
    c.ttl = ttl != null && (isLocal || c.designMode) ? Math.max(0, Number(ttl) || 0) : TTL;
    c.debug = c.designMode || qs.has('sv_reviews_debug');
    // Preview of pending records: theme editor and localhost only. Shoppers can never turn it on.
    c.preview = (isLocal || c.designMode) && (qs.has('sv_reviews_preview') || !!$('script[data-sv-reviews-preview]'));
    c.skus = (c.skus || []).filter(Boolean);
    return c;
  }

  /** Manual reviews emitted by the reviews section (one JSON script per editor block). Only blocks meant for this product are read. */
  function readManual(cfg) {
    var items = [];
    $$('script[data-sv-reviews-manual-item]').forEach(function (el) {
      try { items.push(JSON.parse(el.textContent)); } catch (e) { /* ignore a broken block */ }
    });
    return ingestManual(items, { today: todayIso(), allowLocal: isLocal, extraHosts: (cfg && cfg.hosts) || [] });
  }

  function cacheGet(key) {
    try { var v = JSON.parse(localStorage.getItem(CACHE_PREFIX + key) || 'null'); return v && typeof v.text === 'string' ? v : null; } catch (e) { return null; }
  }
  function cacheSet(key, text) {
    try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), text: text })); } catch (e) { /* quota or private mode */ }
  }

  /** One network fetch per sheet URL per page, shared by every product element on the page. */
  function loadSheet(csvUrl, ttl) {
    if (shared[csvUrl]) return shared[csvUrl];
    shared[csvUrl] = new Promise(function (resolve, reject) {
      var cached = cacheGet(csvUrl);
      var age = cached ? Date.now() - cached.t : Infinity;
      if (cached && age < ttl) return resolve({ text: cached.text, source: 'cache (' + Math.round(age / 1000) + 's old)', ms: 0 });
      var ctl = new AbortController();
      var timer = setTimeout(function () { ctl.abort(); }, 8000);
      var t0 = performance.now();
      fetch(csvUrl, { signal: ctl.signal, credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-cache' }) // our own TTL is the only cache layer: always revalidate with the server
        .then(function (res) { if (!res.ok) throw new Error('http_' + res.status); return res.text(); })
        .then(function (text) {
          if (/^\s*</.test(text)) throw new Error('not_csv (is the sheet shared as "Anyone with the link"?)');
          cacheSet(csvUrl, text);
          resolve({ text: text, source: 'live', ms: Math.round(performance.now() - t0) });
        })
        .catch(function (err) {
          var why = err && err.name === 'AbortError' ? 'timeout after 8s' : (err && err.message || String(err));
          if (cached && age < STALE_MAX) return resolve({ text: cached.text, source: 'stale cache (fetch failed: ' + why + ')', ms: 0 });
          reject(new Error(why));
        })
        .then(function () { clearTimeout(timer); });
    });
    return shared[csvUrl];
  }

  function applyRatingEls(sum) {
    $$('[data-sv-rating]').forEach(function (el) {
      if (!sum.count || state.preview) { el.hidden = true; return; } // the hero rating only ever reflects published reviews
      el.hidden = false;
      var stars = $('[data-sv-rating-stars]', el);
      if (stars) stars.style.setProperty('--p', (sum.average / 5) * 100 + '%');
      var a = $('[data-sv-rating-avg]', el); if (a) a.textContent = fmtAvg(sum.average);
      // "verified" is claimed only when every review counted here is actually verified.
      var label = T((sum.verified === sum.count ? 'rating_verified_' : 'rating_') + (sum.count === 1 ? 'one' : 'other'), { count: sum.count });
      var c = $('[data-sv-rating-count]', el); if (c) c.textContent = label;
      el.setAttribute('aria-label', T('rating_aria', { avg: fmtAvg(sum.average), label: label }));
    });
  }

  var CHECK = function () {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 20 20'); svg.setAttribute('aria-hidden', 'true');
    var p = document.createElementNS(ns, 'path');
    p.setAttribute('d', 'M7.8 13.7L4.4 10.3l1.2-1.2 2.2 2.2 6.5-6.5 1.2 1.2-7.7 7.7z'); p.setAttribute('fill', 'currentColor');
    svg.append(p);
    return svg;
  };

  function boot() {
    api.__booted = true;
    var run = function () {
      var cfg = readConfig();
      state.cfg = cfg;
      var section = $('[data-sv-reviews]');
      $$('[data-sv-rating]').forEach(function (el) {
        if (el.__svBound) return;
        el.__svBound = true;
        el.addEventListener('click', function (e) {
          var t = document.getElementById('customer-reviews');
          if (t && !t.hidden) { e.preventDefault(); t.scrollIntoView({ behavior: 'smooth', block: 'start' }); track('rating_summary_click'); }
        });
      });
      if (!cfg) { if (section) section.hidden = true; return; }
      if (section) bindSection(section);
      var man = readManual(cfg);
      state.manual = man.reviews; state.manualRejects = man.rejects;
      // Preview with no sheet configured: read the bundled file that also holds the pending records (editor/localhost only).
      if (cfg.preview && !qs.get('sv_reviews_url') && (!cfg.url || /sonivra-reviews-none/.test(cfg.url)) && cfg.previewUrl) cfg.url = cfg.previewUrl;
      var n = normalizeUrl(cfg.url, { allowLocal: isLocal });
      if (!cfg.url) { state.error = 'no_url_configured'; return finish(section); }
      if (n.error) { state.error = n.error; return finish(section); }
      state.source = n.kind;
      loadSheet(n.url, cfg.ttl).then(function (res) {
        state.preview = !!cfg.preview;
        var out = ingest(parseCsv(res.text), { skus: cfg.skus, handle: cfg.handle, today: todayIso(), allowLocal: isLocal, extraHosts: cfg.hosts || [], preview: state.preview, photoConfirm: !!$('script[data-sv-reviews-photo-confirm]') || ((isLocal || cfg.designMode) && qs.has('sv_reviews_photo_confirm')) });
        state.sheet = out.reviews; state.stats = out.stats; state.source = res.source; state.fetchMs = res.ms; state.error = out.stats.fatal || '';
        state.loaded = true; finish(section);
      }).catch(function (err) { state.error = String(err && err.message || err); finish(section); });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
    // The theme editor re-renders sections after every edit: rebuild from the cached sheet so manual reviews update live.
    document.addEventListener('shopify:section:load', function () { if (!state.cfg || !$('[data-sv-reviews]')) return; state.rating = 0; state.photos = false; state.verified = false; state.q = ''; state.shown = 6; run(); });
  }

  function finish(section) {
    // One combined dataset: sheet/imported reviews + manual editor reviews. The hero, the count, the distribution and the cards all read state.all.
    state.all = mergeReviews(state.sheet, state.manual);
    var sum = summarize(state.all);
    applyRatingEls(sum);
    api.last = { reviews: state.all, summary: sum, stats: state.stats, error: state.error, source: state.source, product: state.cfg && { handle: state.cfg.handle, skus: state.cfg.skus } };
    if (section) renderSection(section, sum);
    document.dispatchEvent(new CustomEvent('sonivra-reviews:ready', { detail: api.last }));
  }

  /* ---------- Section ---------- */

  var els = {};
  function bindSection(root) {
    els = { root: root, loading: $('[data-rv-loading]', root), empty: $('[data-rv-empty]', root), live: $('[data-rv-live]', root), diag: $('[data-rv-diag]', root),
      avg: $('[data-rv-avg]', root), stars: $('[data-rv-stars]', root), count: $('[data-rv-count]', root), dist: $('[data-rv-dist]', root), list: $('[data-rv-list]', root),
      status: $('[data-rv-status]', root), more: $('[data-rv-more]', root), sort: $('[data-rv-sort]', root), search: $('[data-rv-search]', root), strip: $('[data-rv-strip]', root),
      photosBtn: $('[data-rv-photos]', root), verBtn: $('[data-rv-verified]', root), clear: $('[data-rv-clear]', root), lb: $('[data-rv-lightbox]', root), form: $('[data-rv-dialog]', root) };
    var cfg = state.cfg || {};
    if (els.photosBtn) els.photosBtn.addEventListener('click', function () { state.photos = !state.photos; state.shown = 6; track('review_filter', { f: 'photos' }); draw(); });
    if (els.verBtn) els.verBtn.addEventListener('click', function () { state.verified = !state.verified; state.shown = 6; track('review_filter', { f: 'verified' }); draw(); });
    if (els.sort) els.sort.addEventListener('change', function () { state.sort = els.sort.value; draw(); });
    if (els.more) els.more.addEventListener('click', function () { state.shown += 6; draw(); });
    if (els.clear) els.clear.addEventListener('click', function () { state.rating = 0; state.photos = false; state.verified = false; state.q = ''; if (els.search) els.search.value = ''; state.shown = 6; draw(); });
    if (els.search) {
      var t;
      els.search.addEventListener('input', function () { clearTimeout(t); t = setTimeout(function () { state.q = els.search.value.trim().toLowerCase(); state.shown = 6; draw(); }, 160); });
    }
    $$('[data-rv-open]', root).forEach(function (b) { b.addEventListener('click', function () { if (els.form && els.form.showModal) els.form.showModal(); track('review_write_open'); }); });
    var cancel = $('[data-rv-cancel]', root); if (cancel) cancel.addEventListener('click', function () { els.form.close(); });
    if (els.form && els.form.hasAttribute('data-open')) { try { els.form.showModal(); } catch (e) { /* dialog unsupported */ } }
    var rate = $$('[data-rv-rate]', root);
    rate.forEach(function (r) { r.addEventListener('change', function () { var v = Number(r.value); $$('[data-rv-star]', root).forEach(function (s) { s.setAttribute('data-on', String(Number(s.dataset.rvStar) <= v)); }); }); });
    if (els.lb) {
      $('[data-rv-lb-close]', els.lb).addEventListener('click', function () { els.lb.close(); });
      $('[data-rv-lb-prev]', els.lb).addEventListener('click', function () { lbStep(-1); });
      $('[data-rv-lb-next]', els.lb).addEventListener('click', function () { lbStep(1); });
      els.lb.addEventListener('click', function (e) { if (e.target === els.lb) els.lb.close(); });
      els.lb.addEventListener('keydown', function (e) { if (e.key === 'ArrowRight') lbStep(1); if (e.key === 'ArrowLeft') lbStep(-1); });
    }
    void cfg;
  }

  var gallery = [], lbIndex = 0;
  var okImages = function (r) { return r.images.filter(function (im) { return !state.broken[im.url]; }); };
  function buildGallery() {
    gallery = [];
    state.all.slice().sort(function (a, b) { return a.date < b.date ? 1 : a.date > b.date ? -1 : 0; }).forEach(function (r) { okImages(r).forEach(function (im) { gallery.push({ url: im.url, author: r.author, date: r.date, id: r.id }); }); });
  }
  /** A photo that fails to load is removed everywhere (card, strip, lightbox, Photos filter) instead of showing a broken icon. */
  function imageFailed(url) {
    if (state.broken[url]) return;
    state.broken[url] = true;
    if (els.live && !els.live.hidden) { buildGallery(); if (!gallery.length) state.photos = false; draw(); }
  }
  function lbShow() {
    var p = gallery[lbIndex]; if (!p) return;
    var img = $('img', els.lb); img.onerror = function () { imageFailed(p.url); if (els.lb.open) { if (gallery.length) { lbIndex = Math.min(lbIndex, gallery.length - 1); lbShow(); } else els.lb.close(); } }; img.src = p.url; img.alt = T('photo_alt', { author: p.author });
    $('[data-rv-lb-cap]', els.lb).textContent = T('photo_caption', { author: p.author, date: fmtDate(p.date), n: lbIndex + 1, total: gallery.length });
    $('[data-rv-lb-prev]', els.lb).hidden = gallery.length < 2; $('[data-rv-lb-next]', els.lb).hidden = gallery.length < 2;
  }
  function lbStep(d) { if (!gallery.length) return; lbIndex = (lbIndex + d + gallery.length) % gallery.length; lbShow(); }
  function lbOpen(i) { if (!els.lb || !els.lb.showModal) return; lbIndex = i; lbShow(); els.lb.showModal(); track('review_photo_open'); }

  function visible() {
    var L = state.all.slice();
    if (state.rating) L = L.filter(function (r) { return r.rating === state.rating; });
    if (state.photos) L = L.filter(function (r) { return okImages(r).length; });
    if (state.verified) L = L.filter(function (r) { return r.verified; });
    if (state.q) L = L.filter(function (r) { return (r.title + ' ' + r.body + ' ' + r.author).toLowerCase().indexOf(state.q) >= 0; });
    var cmp = {
      newest: function (a, b) { return a.date < b.date ? 1 : a.date > b.date ? -1 : 0; },
      highest: function (a, b) { return b.rating - a.rating || (a.date < b.date ? 1 : -1); },
      lowest: function (a, b) { return a.rating - b.rating || (a.date < b.date ? 1 : -1); }
    }[state.sort];
    return L.sort(cmp);
  }

  function card(r) {
    var body = h('p', { class: 'svrv-card__body', text: r.body });
    var long = r.body.length > 420;
    if (long) body.dataset.clamp = 'true';
    var more = long ? h('button', { type: 'button', class: 'svrv-card__more', 'aria-expanded': 'false', text: T('read_more'), onclick: function (e) {
      var open = body.dataset.clamp !== 'true';
      body.dataset.clamp = open ? 'true' : 'false';
      e.currentTarget.textContent = open ? T('read_more') : T('show_less');
      e.currentTarget.setAttribute('aria-expanded', String(!open));
    } }) : null;
    var shown = okImages(r);
    var imgs = shown.length ? h('div', { class: 'svrv-card__imgs' }, shown.map(function (im, i) {
      return h('button', { type: 'button', class: 'svrv-card__img', 'aria-label': T('view_photo', { n: i + 1, author: r.author }), onclick: function () {
        var gi = gallery.findIndex(function (g) { return g.url === im.url && g.id === r.id; });
        lbOpen(Math.max(0, gi));
      } }, h('img', { src: thumb(im.url, 560), alt: '', loading: 'lazy', decoding: 'async', referrerpolicy: 'no-referrer', onerror: function () { imageFailed(im.url); } }));
    })) : null;
    var hp = r.heldPhotos && state.cfg && state.cfg.debug ? h('p', { class: 'svrv-card__pv' }, h('b', { text: 'Editor note: ' }), r.heldPhotos + (r.heldPhotos === 1 ? ' photo is' : ' photos are') + ' on file but hidden until photo permission is confirmed (Sonivra reviews section setting).') : null;
    var pv = r.preview ? h('p', { class: 'svrv-card__pv' }, h('b', { text: 'Not publishable yet: ' }), r.preview.flags.concat(['purchase not verified']).join(' · ')) : null;
    var stars = h('span', { class: 'svrv-stars', role: 'img', 'aria-label': T('stars_aria', { rating: r.rating }) });
    stars.style.setProperty('--p', (r.rating / 5) * 100 + '%');
    return h('article', { class: 'svrv-card' },
      h('div', { class: 'svrv-card__head' },
        h('div', { class: 'svrv-card__avatar', 'aria-hidden': 'true', text: initials(r.author) }),
        h('div', { class: 'svrv-card__who' }, h('b', { text: r.author }), h('span', { text: fmtDate(r.date) })),
        r.verified ? h('span', { class: 'svrv-badge' }, CHECK(), T('verified')) : null),
      h('div', null, stars),
      r.title ? h('h3', { text: r.title }) : null, body, more, imgs, pv, hp);
  }

  function renderDiag() {
    if (!els.diag) return;
    var c = state.cfg || {};
    els.diag.hidden = !c.debug;
    if (!c.debug) return;
    var s = state.stats, rej = s ? Object.keys(s.rejects).map(function (k) { return k + ': ' + s.rejects[k]; }).join(', ') || 'none' : 'n/a';
    var skus = s ? Object.keys(s.bySku).slice(0, 8).map(function (k) { return k + '×' + s.bySku[k]; }).join(', ') : 'n/a';
    els.diag.textContent = ['Reviews diagnostics (hidden from shoppers)',
      'product: ' + c.handle + ' | SKUs: ' + (c.skus.join(', ') || '(none; matching by product_handle)'),
      'source: ' + (state.source || 'n/a') + (state.fetchMs ? ' in ' + state.fetchMs + ' ms' : '') + (state.error ? ' | ERROR: ' + state.error : ''),
      'rows in sheet: ' + (s ? s.rows : 'n/a') + ' | for this product: ' + (s ? s.forProduct + ' (by SKU ' + s.matchedBy.sku + ', by handle ' + s.matchedBy.handle + ')' : 'n/a') + ' | other products: ' + (s ? s.otherProducts : 'n/a') + ' | no SKU and no handle: ' + (s ? s.missingSku : 'n/a'),
      'published here: ' + state.all.length + ' | rejected: ' + rej + ' | images dropped (no rights / host): ' + (s ? s.imagesDropped : 'n/a'),
      'manual (theme editor) reviews shown: ' + state.manual.length + (state.manualRejects.length ? ' | NOT shown: ' + state.manualRejects.map(function (r) { return r.id.replace('manual-', 'block ') + ' (' + r.why + ')'; }).join('; ') : ''),
      'SKUs found in sheet: ' + skus].join('\n');
  }

  function renderSection(root, sum) {
    var c = state.cfg;
    renderDiag();
    var show = function (el, on) { if (el) el.hidden = !on; };
    show(els.loading, false);
    var hasUrl = !!(c && c.url);
    if (!sum.count) {
      // No approved reviews for this product: compact, honest state. Hidden entirely when no sheet is configured or the sheet failed.
      var showEmpty = state.loaded && !state.error;
      show(els.live, false); show(els.empty, showEmpty);
      var posted = !!$('[data-rv-posted]', root);
      root.hidden = !(showEmpty || c.debug || posted);
      if (!hasUrl && !c.debug && !posted) root.hidden = true;
      return;
    }
    root.hidden = false; show(els.empty, false); show(els.live, true);
    els.avg.textContent = fmtAvg(sum.average);
    els.stars.style.setProperty('--p', (sum.average / 5) * 100 + '%');
    els.count.textContent = T(sum.count === 1 ? 'based_on_one' : 'based_on_other', { count: sum.count });
    buildGallery();
    var pvBanner = $('[data-rv-preview]', root);
    if (state.preview) {
      if (!pvBanner) { pvBanner = h('div', { class: 'svrv-alert svrv-alert--err', 'data-rv-preview': '', role: 'note' }); els.live.before(pvBanner); }
      pvBanner.replaceChildren(h('b', { text: 'PREVIEW, NOT PUBLISHED. ' }), 'These ' + sum.count + ' records are pending or unverified and are shown only to you (theme editor / localhost). Shoppers see none of them, and the star rating near the product title stays hidden.');
    } else if (pvBanner) pvBanner.remove();
    draw();
  }

  function draw() {
    var sum = summarize(state.all);
    els.dist.replaceChildren.apply(els.dist, [5, 4, 3, 2, 1].map(function (n) {
      var bar = h('span', { class: 'svrv-bar' }, h('i'));
      bar.firstChild.style.width = (sum.dist[n] / sum.count) * 100 + '%';
      return h('button', { type: 'button', class: 'svrv-row', 'aria-pressed': String(state.rating === n), 'aria-label': T('filter_stars_aria', { n: n, count: sum.dist[n] }), disabled: sum.dist[n] === 0 && state.rating !== n,
        onclick: function () { state.rating = state.rating === n ? 0 : n; state.shown = 6; track('review_filter', { f: 'rating' }); draw(); } },
        h('span', { text: T('star_label', { n: n }) }), bar, h('span', { text: String(sum.dist[n]) }));
    }));
    var hasPhotos = gallery.length > 0, hasVer = state.all.some(function (r) { return r.verified; });
    if (!hasPhotos) state.photos = false;
    els.photosBtn.hidden = !hasPhotos; els.photosBtn.setAttribute('aria-pressed', String(state.photos));
    els.verBtn.hidden = !hasVer; els.verBtn.setAttribute('aria-pressed', String(state.verified));
    if (els.strip) {
      els.strip.hidden = !hasPhotos;
      if (hasPhotos) {
        var max = 8;
        els.strip.replaceChildren.apply(els.strip, gallery.slice(0, max).map(function (p, i) {
          var extra = i === max - 1 && gallery.length > max ? gallery.length - max + 1 : 0;
          return h('li', null, h('button', { type: 'button', class: 'svrv-strip__b', 'aria-label': T('open_photo', { n: i + 1, total: gallery.length }), onclick: function () { lbOpen(i); } },
            h('img', { src: thumb(p.url, 320), alt: '', loading: 'lazy', decoding: 'async', referrerpolicy: 'no-referrer', onerror: function () { imageFailed(p.url); } }), extra ? h('span', { class: 'svrv-strip__more', text: '+' + extra }) : null));
        }));
      }
    }
    var L = visible(), page = L.slice(0, L.length <= 8 ? L.length : state.shown);
    var filtered = L.length !== state.all.length;
    els.clear.hidden = !(state.rating || state.photos || state.verified || state.q);
    els.list.replaceChildren.apply(els.list, page.length ? page.map(card) : [h('div', { class: 'svrv-none', text: T('none_match') })]);
    els.status.textContent = filtered ? T('showing_filtered', { shown: page.length, total: L.length, all: state.all.length }) : T('showing', { shown: page.length, total: L.length });
    els.more.hidden = L.length <= page.length;
    els.more.textContent = T('show_more', { n: Math.min(6, L.length - page.length) });
  }

  var api = core;
  api.boot = boot;
  api.__booted = false;
  api.state = state;
  api.imageFailed = imageFailed;
  return api;
});
